import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLQuotes = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Text Content & Typography</div>
                <h1>Quotations & Citations</h1>
                <p className="hero-lead">
                    Learn to properly quote and cite content using HTML's semantic elements. Master blockquotes,
                    inline quotes, citations, and give proper credit to sources.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Proper Quoting Matters</h2>
                <p>
                    Using semantic quote elements improves readability, SEO, and gives proper attribution to sources.
                </p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Academic Integrity</h3>
                        <p>Properly credit sources and avoid plagiarism</p>
                    </div>
                    <div className="benefit-card">
                        <h3> SEO Benefits</h3>
                        <p>Search engines recognize quoted content as references</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Accessibility</h3>
                        <p>Screen readers announce quotes with proper context</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Readability</h3>
                        <p>Visual distinction helps readers identify quoted content</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>The &lt;blockquote&gt; Element</h2>
                <p>
                    The <code>&lt;blockquote&gt;</code> element is for long quotations that form a separate paragraph or section.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Blockquote</span>
                    </div>
                    <CodeBlock language="html" code={`<blockquote>
    <p>
        The only way to do great work is to love what you do. 
        If you haven't found it yet, keep looking. Don't settle.
    </p>
</blockquote>`} />
                </div>

                <div className="render-preview">
                    <h3>Renders as:</h3>
                    <blockquote style={{ borderLeft: '4px solid #ccc', paddingLeft: '1rem', margin: '1rem 0', fontStyle: 'italic' }}>
                        <p>
                            The only way to do great work is to love what you do.
                            If you haven't found it yet, keep looking. Don't settle.
                        </p>
                    </blockquote>
                </div>

                <h3>Adding Source Attribution</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span>Blockquote with Citation</span>
                    </div>
                    <CodeBlock language="html" code={`<blockquote cite="https://www.example.com/article">
    <p>
        The only way to do great work is to love what you do. 
        If you haven't found it yet, keep looking. Don't settle.
    </p>
    <footer>
         <cite>Steve Jobs</cite>, Stanford Commencement Address (2005)
    </footer>
</blockquote>`} />
                </div>

                <div className="render-preview">
                    <h3>Renders as:</h3>
                    <blockquote style={{ borderLeft: '4px solid #ccc', paddingLeft: '1rem', margin: '1rem 0', fontStyle: 'italic' }}>
                        <p>
                            The only way to do great work is to love what you do.
                            If you haven't found it yet, keep looking. Don't settle.
                        </p>
                        <footer style={{ fontStyle: 'normal', marginTop: '0.5rem' }}>
                             <cite>Steve Jobs</cite>, Stanford Commencement Address (2005)
                        </footer>
                    </blockquote>
                </div>

                <div className="attribute-explanation">
                    <h3>The <code>cite</code> Attribute:</h3>
                    <ul>
                        <li>Contains a URL to the source document</li>
                        <li>Not displayed visually (for machines, not humans)</li>
                        <li>Helps search engines understand the quote's origin</li>
                        <li>Should contain a full URL</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The &lt;q&gt; Element (Inline Quotes)</h2>
                <p>
                    The <code>&lt;q&gt;</code> element is for short, inline quotations within a sentence.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Inline Quote Example</span>
                    </div>
                    <CodeBlock language="html" code={`<p>
    As Benjamin Franklin once said, 
    <q>Tell me and I forget, teach me and I may remember, involve me and I learn.</q>
</p>`} />
                </div>

                <div className="render-preview">
                    <h3>Renders as:</h3>
                    <p>
                        As Benjamin Franklin once said,
                        <q>Tell me and I forget, teach me and I may remember, involve me and I learn.</q>
                    </p>
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Automatic Quotation Marks:</strong> Browsers automatically add quotation marks around
                        <code>&lt;q&gt;</code> content. The style varies by language (e.g., "English" vs. French).
                    </div></ProTip>

                <h3>With Source Citation</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<p>
    According to the W3C, 
    <q cite="https://www.w3.org/standards/">
        The Web is designed to work for all people, whatever their hardware, 
        software, language, location, or ability.
    </q>
</p>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The &lt;cite&gt; Element</h2>
                <p>
                    The <code>&lt;cite&gt;</code> element represents the title of a creative work being referenced
                    (book, article, movie, song, etc.).
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Citing Creative Works</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Citing a book --&gt;
<p>My favorite novel is <cite>The Great Gatsby</cite> by F. Scott Fitzgerald.</p>

<!-- Citing a movie --&gt;
<p>I recently watched <cite>The Shawshank Redemption</cite>.</p>

<!-- Citing an article --&gt;
<p>Read more in <cite>The New York Times</cite> article about climate change.</p>

<!-- Citing a song --&gt;
<p>The song <cite>Imagine</cite> by John Lennon is timeless.</p>

<!-- Citing within a blockquote --&gt;
<blockquote>
    <p>Quote text here...</p>
    <footer>
         From <cite>HTML: The Definitive Guide</cite> by Chuck Musciano
    </footer>
</blockquote>`} />
                </div>

                <div className="render-preview">
                    <h3>Examples:</h3>
                    <p>My favorite novel is <cite>The Great Gatsby</cite> by F. Scott Fitzgerald.</p>
                    <p>I recently watched <cite>The Shawshank Redemption</cite>.</p>
                </div>

                <Warning><strong> Common Mistake:</strong> Don't use <code>&lt;cite&gt;</code> for people's names.
                    Use it only for titles of works. For people, just use normal text or <code>&lt;strong&gt;</code>.</Warning>

                <div className="code-example">
                    <div className="code-header">
                        <span className="incorrect"> Wrong</span>
                    </div>
                    <CodeBlock language="html" code={`<p>Quote by <cite>Steve Jobs</cite></p>  <!-- Wrong! --&gt;`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Correct</span>
                    </div>
                    <CodeBlock language="html" code={`<p>Quote by <strong>Steve Jobs</strong> from <cite>Stanford Address</cite></p>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Quote Examples</h2>

                <h3>Academic Quote</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<blockquote cite="https://www.example.com/research-paper">
    <p>
        Climate change represents one of the greatest challenges of our time. 
        Immediate action is required to mitigate its effects and protect future generations.
    </p>
    <footer>
         Dr. Jane Smith, 
        <cite>Climate Science Review</cite>, 
        Vol. 45, No. 3 (2024), pp. 123-145
    </footer>
</blockquote>`} />
                </div>

                <h3>Literary Quote</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<blockquote>
    <p>
        It is a truth universally acknowledged, that a single man in possession 
        of a good fortune, must be in want of a wife.
    </p>
    <footer>
         Jane Austen, <cite>Pride and Prejudice</cite> (1813)
    </footer>
</blockquote>`} />
                </div>

                <h3>Multiple Paragraph Quote</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<blockquote cite="https://example.com/article">
    <p>
        First paragraph of the quotation. This contains the opening thoughts 
        of the author on the subject matter.
    </p>
    <p>
        Second paragraph continuing the quotation. Here the author expands 
        on their initial point with additional evidence.
    </p>
    <footer>
         Author Name, <cite>Article Title</cite> (2025)
    </footer>
</blockquote>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Styling Quotes with CSS</h2>
                <p>
                    Customize the appearance of quotes to match your design:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Professional Quote Styling</span>
                    </div>
                    <pre><code>{`/* Blockquote styling */
blockquote {
    margin: 2rem 0;
    padding: 1rem 1.5rem;
    border-left: 4px solid #007bff;
    background-color: #f8f9fa;
    font-style: italic;
    position: relative;
}

/* Add opening quote mark */
blockquote::before {
    content: "\\\"";
    font-size: 4rem;
    color: #007bff;
    position: absolute;
    left: 10px;
    top: -10px;
    opacity: 0.3;
}

/* Footer styling */
blockquote footer {
    margin-top: 1rem;
    font-style: normal;
    font-size: 0.9rem;
    color: #6c757d;
}

/* Cite styling */
cite {
    font-style: italic;
    font-weight: 600;
}

/* Inline quote styling */
q {
    font-style: italic;
    color: #495057;
}

/* Custom quotation marks for inline quotes */
q::before {
    content: open-quote;
    font-weight: bold;
}

q::after {
    content: close-quote;
    font-weight: bold;
}`}</code></pre>
                </div>

                <h3>Fancy Quote Design</h3>
                <div className="code-example">
                    <pre><code>{`/* Card-style quote */
blockquote.fancy {
    margin: 2rem auto;
    max-width: 600px;
    padding: 2rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
    font-size: 1.2rem;
    line-height: 1.6;
    position: relative;
}

blockquote.fancy::before {
    content: "\\\"";
    font-size: 6rem;
    position: absolute;
    top: -20px;
    left: 20px;
    color: rgba(255,255,255,0.2);
}

blockquote.fancy footer {
    margin-top: 1.5rem;
    text-align: right;
    font-size: 1rem;
    font-weight: 300;
}`}</code></pre>
                </div>
            </div>

            <div className="content-section">
                <h2>The &lt;abbr&gt; Element (Abbreviations)</h2>
                <p>
                    While not strictly for quotes, <code>&lt;abbr&gt;</code> is related and often used alongside citations.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Abbreviation with Tooltip</span>
                    </div>
                    <CodeBlock language="html" code={`<p>
    The <abbr title="World Wide Web Consortium">W3C</abbr> maintains web standards.
</p>

<p>
    Use <abbr title="HyperText Markup Language">HTML</abbr> for structure and 
    <abbr title="Cascading Style Sheets">CSS</abbr> for styling.
</p>

<!-- In a citation --&gt;
<p>
    Published in the <abbr title="Journal of Computer Science">JCS</abbr>, Vol. 12
</p>`} />
                </div>

                <div className="render-preview">
                    <h3>Renders as (hover to see tooltip):</h3>
                    <p>
                        The <abbr title="World Wide Web Consortium">W3C</abbr> maintains web standards.
                    </p>
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility Considerations</h2>

                <div className="accessibility-tips">
                    <h3>Screen Reader Behavior:</h3>
                    <ul>
                        <li>
                            <strong>Blockquotes:</strong> Announced as "blockquote" when entering and "end blockquote" when exiting
                        </li>
                        <li>
                            <strong>Inline quotes (<code>&lt;q&gt;</code>):</strong> Quotation marks are added and announced
                        </li>
                        <li>
                            <strong>Citations:</strong> Announced as "cite" to indicate it's a title
                        </li>
                        <li>
                            <strong>Abbreviations:</strong> Title attribute is read when focused
                        </li>
                    </ul>
                </div>

                <div className="tip-box">
                    <strong> Best Practices:</strong>
                    <ul>
                        <li>Always provide source attribution for quotes</li>
                        <li>Use <code>cite</code> attribute with URLs for machine-readability</li>
                        <li>Use <code>&lt;cite&gt;</code> element for human-readable titles</li>
                        <li>Don't rely solely on visual styling to indicate quotes</li>
                        <li>Provide context before the quote when possible</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Real-World Use Cases</h2>

                <h3>Blog Article with Quotes</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<article>
    <h2>The Impact of Technology on Education</h2>
    
    <p>
        Technology has transformed modern education in unprecedented ways. 
        As educational expert Dr. Maria Johnson notes, 
        <q>Digital tools have democratized access to knowledge like never before.</q>
    </p>

    <blockquote cite="https://example.com/education-report-2024">
        <p>
            Students who use educational technology show a 23% improvement in 
            engagement and a 15% increase in test scores compared to traditional 
            methods alone.
        </p>
        <footer>
             <cite>Global Education Technology Report</cite> (2024)
        </footer>
    </blockquote>

    <p>
        These findings align with earlier research published in 
        <cite>The Journal of Educational Psychology</cite>, which found similar benefits.
    </p>
</article>`} />
                </div>

                <h3>Product Review with Customer Quotes</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<section class="reviews">
    <h3>Customer Reviews</h3>
    
    <blockquote>
        <p>
            This product exceeded all my expectations. The quality is outstanding 
            and customer service was incredibly helpful.
        </p>
        <footer>
             Sarah M., Verified Buyer
        </footer>
    </blockquote>

    <blockquote>
        <p>
            Best purchase I've made this year. Highly recommend to anyone looking 
            for a reliable solution.
        </p>
        <footer>
             John D., Verified Buyer
        </footer>
    </blockquote>
</section>`} />
                </div>

                <h3>FAQ Section</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<section class="faq">
    <h2>Frequently Asked Questions</h2>
    
    <article>
        <h3>What is your return policy?</h3>
        <p>
            As stated in our <cite>Terms of Service</cite>, we offer a 30-day 
            money-back guarantee. Simply contact our support team to initiate a return.
        </p>
    </article>

    <article>
        <h3>How long does shipping take?</h3>
        <p>
            According to our <cite>Shipping Policy</cite>, most orders arrive 
            within 5-7 business days for standard shipping.
        </p>
    </article>
</section>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>&lt;blockquote&gt;</code> for long quotations</li>
                            <li>Use <code>&lt;q&gt;</code> for short inline quotes</li>
                            <li>Always attribute sources properly</li>
                            <li>Use <code>cite</code> attribute for source URLs</li>
                            <li>Use <code>&lt;cite&gt;</code> element for work titles</li>
                            <li>Provide context around quotes</li>
                            <li>Use <code>&lt;abbr&gt;</code> for abbreviations with titles</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use quotes for emphasis (use <code>&lt;em&gt;</code> or <code>&lt;strong&gt;</code>)</li>
                            <li>Forget to cite sources</li>
                            <li>Use <code>&lt;cite&gt;</code> for people's names</li>
                            <li>Use manual quotation marks with <code>&lt;q&gt;</code></li>
                            <li>Use quotes purely for styling</li>
                            <li>Plagiarize by not attributing sources</li>
                            <li>Mix up <code>cite</code> attribute and <code>&lt;cite&gt;</code> element</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLQuotes;



